/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import * as Sentry from '@sentry/react';
import type { HTTPError } from 'ky';
import { type ErrorResponse } from 'react-router-dom';
import type { ZodError } from 'zod';

import { isCustomJsonResponse, type CustomJsonResponse } from '@fai/third-party/react-router-dom';

export enum SentryTags {
  ERROR_TYPE = 'error-type',
}

export enum ErrorType {
  GRAPHQL = 'graphQL',
  LOADER = 'loader',
  VALIDATION = 'validation',
  NETWORK = 'network',
  KY = 'ky',
}

export function reportZodErrorToSentry(error: ZodError) {
  Sentry.withScope((scope) => {
    scope.clear();
    scope
      .setLevel('error')
      .setTag(SentryTags.ERROR_TYPE, ErrorType.VALIDATION)
      .setContext('detail', { error });
    Sentry.captureException(error.message);
  });
}

export function reportLoaderErrorToSentry(error: ErrorResponse | CustomJsonResponse) {
  Sentry.withScope((scope) => {
    scope.clear();
    scope.setLevel('error').setTag(SentryTags.ERROR_TYPE, ErrorType.LOADER);

    const errorMessage = isCustomJsonResponse(error) ? error.data.message : error.data;
    scope.setContext('detail', errorMessage);

    Sentry.captureException(errorMessage);
  });
}

export function reportHTTPErrorToSentry(error: HTTPError) {
  Sentry.withScope((scope) => {
    scope.clear();
    scope.setLevel('error').setTag(SentryTags.ERROR_TYPE, ErrorType.KY);
    scope.setContext('detail', { body: error.options.body });

    Sentry.captureException(error);
  });
}
